import {
  DUTY_ANALYTICS_CARD_KINDS,
  type DutyAnalyticsCardDto,
  type DutyAnalyticsCardKind,
  type DutyAnalyticsLayout,
} from '@pams/shared';
import { Fragment, type ReactNode } from 'react';
import { TOTAL_CELL, cellFor, drillHref, type DrillCell, type DutyAnalyticsWindowParam } from '@/lib/dutyAnalytics';
import { DA_CARD_GRID } from '@/lib/dutyAnalyticsLayout';
import { DUTY_ANALYTICS_COPY, DUTY_ANALYTICS_NOUN } from '../copy';
import { cardInfo } from '../figureInfo';
import { CountLink } from './CountLink';
import { FigureInfo } from './FigureInfo';
import { SectionFailure } from './SectionFailure';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2e] THE FOUR HEADLINE CARDS, IN `DUTY_ANALYTICS_CARD_KINDS` ORDER.
 *
 *  ⚠️ THE SERVER PICKED THEM. "Best mapping" is decided there by integer cross-multiplication among
 *  rows asked for at least the median (U4), so this file renders counts and divides nothing: the card
 *  reads "210 of 212 named", and the floor beneath it says, in counts, who was compared.
 *
 *  ⚠️ A PICK OF `null` IS A SENTENCE, NEVER "0 of 212": when nothing qualifying named anyone the
 *  server crowns nobody, and the card says so.
 *
 *  ⚠️ EVERY COUNT OPENS THE CELL IT CAME FROM — a picked division's or unit's row, or the view's own
 *  total — through the same `cellFor` the tables use, so a card and the table row beside it cannot
 *  lead to two different lists.
 *
 *  ⚠️ [P7C.1 §3] EACH CARD'S INFO BUTTON SITS BESIDE ITS `<h3>`, NEVER INSIDE IT — inside, it would join the
 *  heading's name. It carries the notes for the figures the card shows (`cardInfo`).
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = DUTY_ANALYTICS_COPY;

const Card = ({
  kind,
  subject,
  children,
}: {
  kind: DutyAnalyticsCardKind;
  subject: string | null;
  children: ReactNode;
}) => (
  <article
    data-card={kind}
    data-testid={`duty-analytics-card-${kind}`}
    className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-card p-5"
  >
    <div className="flex items-start gap-1.5">
      <h3 className="text-sm font-medium text-muted-foreground">{copy.cards[kind]}</h3>
      <FigureInfo infoKey={kind} title={copy.cards[kind]} content={cardInfo(kind)} />
    </div>
    {subject !== null && <p className="truncate text-base font-medium text-heading">{subject}</p>}
    {children}
  </article>
);

/** Where every count on a card leads from: the window, and the page's narrowing or the division read AS. */
type CountContext = {
  readonly range: DutyAnalyticsWindowParam;
  readonly pageDivision: string | undefined;
  readonly asDivisionId: string | undefined;
};

const Count = ({
  value,
  metric,
  cell,
  name,
  context,
}: {
  value: number;
  metric: Parameters<typeof drillHref>[0];
  cell: DrillCell;
  name: string;
  context: CountContext;
}) => (
  <CountLink
    value={value}
    metric={metric}
    href={drillHref(metric, cell, context.range, context.pageDivision, context.asDivisionId)}
    label={copy.countLabel(metric, value, name)}
  />
);

export const HeadlineCards = ({
  cards,
  layout,
  range,
  pageDivision,
  asDivisionId,
  scopeLabel,
}: {
  /** `null` when the fill section could not be computed — the cards fail with it. */
  cards: readonly DutyAnalyticsCardDto[] | null;
  layout: DutyAnalyticsLayout;
  range: DutyAnalyticsWindowParam;
  /** The page's own `?division=`, carried into every drill link for the way back (`drillHref`). */
  pageDivision?: string;
  /** [P7C.1 §4] The division this page is read AS, from the commissionerate — sent with every drill. */
  asDivisionId?: string;
  /** What the view's own total is called: "All divisions", or "<division> total". */
  scopeLabel: string;
}) => {
  const noun = DUTY_ANALYTICS_NOUN[layout];
  const context: CountContext = { range, pageDivision, asDivisionId };

  const render = (card: DutyAnalyticsCardDto): ReactNode => {
    switch (card.kind) {
      case 'MOST_ASKED': {
        if (card.pick === null) {
          return (
            <Card kind={card.kind} subject={null}>
              <p className="text-sm text-muted-foreground">{copy.nothingAsked}</p>
            </Card>
          );
        }
        const name = card.pick.orgUnit?.name ?? copy.unknownUnit;
        const cell = cellFor(layout, 'rows', { orgUnitId: card.pick.orgUnitId, divisionId: null });
        return (
          <Card kind={card.kind} subject={name}>
            <p className="text-sm" data-testid="duty-analytics-card-figure">
              <Count value={card.pick.asked} metric="ASKED" cell={cell} name={name} context={context} />{' '}
              {copy.dutiesAsked}
            </p>
          </Card>
        );
      }
      case 'BEST_MAPPING': {
        const floor =
          card.floor === null ? null : (
            <p className="text-xs text-muted-foreground" data-testid="duty-analytics-card-floor">
              {copy.floor(noun, card.floor.askedAtLeast, card.floor.qualifying, card.floor.considered)}
            </p>
          );
        if (card.pick === null) {
          return (
            <Card kind={card.kind} subject={null}>
              <p className="text-sm text-muted-foreground">
                {card.floor === null ? copy.nothingAsked : copy.nobodyNamed(noun.one)}
              </p>
              {floor}
            </Card>
          );
        }
        const name = card.pick.orgUnit?.name ?? copy.unknownUnit;
        const cell = cellFor(layout, 'rows', { orgUnitId: card.pick.orgUnitId, divisionId: null });
        return (
          <Card kind={card.kind} subject={name}>
            <p className="text-sm" data-testid="duty-analytics-card-figure">
              <Count value={card.pick.named} metric="NAMED" cell={cell} name={name} context={context} />{' '}
              {copy.ofNamedJoin}{' '}
              <Count value={card.pick.asked} metric="ASKED" cell={cell} name={name} context={context} /> {copy.named}
            </p>
            {floor}
          </Card>
        );
      }
      case 'ASKED_AND_NAMED':
        return (
          <Card kind={card.kind} subject={scopeLabel}>
            <p className="text-sm" data-testid="duty-analytics-card-figure">
              <Count value={card.asked} metric="ASKED" cell={TOTAL_CELL} name={scopeLabel} context={context} />{' '}
              {copy.dutiesAsked}
            </p>
            <p className="text-sm">
              <Count value={card.named} metric="NAMED" cell={TOTAL_CELL} name={scopeLabel} context={context} />{' '}
              {copy.named}
            </p>
          </Card>
        );
      case 'OPEN_AND_DECLARED':
        return (
          <Card kind={card.kind} subject={scopeLabel}>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="duty-analytics-card-figure">
              <p>
                <Count value={card.leftOpen} metric="LEFT_OPEN" cell={TOTAL_CELL} name={scopeLabel} context={context} />{' '}
                {copy.sharesLeftOpen}
              </p>
              <p>
                <Count
                  value={card.declaredShort}
                  metric="DECLARED_SHORT"
                  cell={TOTAL_CELL}
                  name={scopeLabel}
                  context={context}
                />{' '}
                {copy.shortfallsDeclared}
              </p>
            </div>
          </Card>
        );
    }
  };

  return (
    <section data-testid="duty-analytics-cards" aria-label={copy.cardsLabel}>
      {cards === null ? (
        <SectionFailure />
      ) : (
        <div className={DA_CARD_GRID}>
          {DUTY_ANALYTICS_CARD_KINDS.map((kind) => {
            const card = cards.find((candidate) => candidate.kind === kind);
            return card === undefined ? null : <Fragment key={kind}>{render(card)}</Fragment>;
          })}
        </div>
      )}
    </section>
  );
};
